import React, { useEffect, useState } from 'react';
import { connect, useSelector } from 'react-redux';
import { Button, Col, Row } from 'reactstrap';
import DateUtility from '../../blocks/utils/date-utility';
import NumberUtility from '../../blocks/utils/number-utility';
import Coupon from '../../models/coupon/Coupon';
import { CouponStatusType } from '../../models/coupon/CouponStatusType';
import { ApplicationState } from '../../store';
import PrometTable from '../shared/promet-table/PrometTable';
import ContentLayout from '../core/ContentLayout';
import HorizontalDivider from '../shared/HorizontalDivider';
import icon_my_coupons from '../../assets/icons/2x/icon_moji-kuponi.png';
import icon_baseline_error from '../../assets/icons/2x/baseline-error-24px.png';
import PrometSelect from '../shared/PrometSelect';
import CouponSearchItem from './search/CouponSearchItem';
import { searchCouponsActions } from '../../store/tariff/actions';
import TrafficAreaUtility from '../../blocks/utils/traffic-area-utility/TrafficAreaUtility';
import { SearchCouponsRequest } from '../../models/tariff/SearchCouponsRequest';
import { ProfileQuery } from '../../models/tariff/queries/ProfileQuery';
import { OrganizationQuery } from '../../models/tariff/queries/OrganizationQuery';
import I18n from '../../blocks/localization';
import { SubsidyCouponQuery } from '../../models/tariff/queries/SubsidyCouponQuery';
import { ContractCouponQuery } from '../../models/tariff/queries/ContractCouponQuery';
import PrometModal from '../shared/Modal';
import { checkCouponForWarnings } from '../../blocks/utils/coupon-utility/coupon-utility';
import { ClientSubsidy } from '../../models/client/ClientSubsidy';
import ContractClientTariff from '../../models/tariff/ContractClientTariff';
import { purchaseCouponActions } from '../../store/coupon/actions';
import Toast from '../shared/Toast';
import EuroUtility from '../../blocks/utils/euro-utility';
import CurrencyUtility from '../../blocks/utils/currency-utility/CurrencyUtility';
import { ClientProfile } from '../../models/profile/ClientProfile';
import { refreshProfilesActions } from '../../store/account/actions';

type Props = typeof mapDispatchToProps;

const MyCoupons = (props: Props) => {
    const [startAdministrativeArea, setFromJLS] = useState<number | undefined>(undefined);
    const [endAdministrativeArea, setToJLS] = useState<number | undefined>(undefined);
    const [initialSearchDone, setInitialSearchDone] = useState<boolean | undefined>(false);
    const [subsidisedCouponsMode, setSubsidisedCouponsMode] = useState<boolean | undefined>(false);
    const [showWarning, setShowWarning] = useState<boolean | undefined>(false);
    const [selectedCoupon, setSelectedCoupon] = useState<Coupon | undefined>(undefined);
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    

    const client = useSelector((state: ApplicationState) => state.client.client);
    const clientCoupons = useSelector((state: ApplicationState) => state.client.coupons);
    const clientSubsidies = useSelector((state: ApplicationState) => state.client.subsidies);
    const trafficAreas = useSelector((state: ApplicationState) => state.tariff.trafficAreas);
    const couponsSearchResult = useSelector((state: ApplicationState) => state.tariff.couponsSearchResult);
    const isFullProfileActivationInProgress = useSelector((state: ApplicationState) => state.account.account?.isFullProfileActivationInProgress);

    const validCoupons = clientCoupons?.filter(x => x.status === CouponStatusType.CurrentlyValid || x.status === CouponStatusType.NotYetValid);
    const canBuyCoupon = client && client.clientProfiles?.length > 0;

    const clientProfile = getActiveOrFutureFirstActiveProfile(client?.clientProfiles);

    const getTicketPeriodName = (validFrom?: Date, validTo?: Date): string => {
        if (new Date(String(validFrom))?.getMonth() === new Date(String(validTo))?.getMonth() && new Date(String(validFrom))?.getFullYear() === new Date(String(validTo))?.getFullYear())
            return I18n.t('periodNameMontly');

        return I18n.t('periodNameYearly');
    }

    const trafficAreaOptions = TrafficAreaUtility.getAsDdl(trafficAreas);

    const getModalBody = () => (
        <div className="d-flex flex-column gap-2">
          <p className="mb-0 text-muted">
            {I18n.t("myProfile.tabs.myProfile.profiles.info")}
          </p>
        </div>
      );

    const checkStatus = () => {
        if (!client?.id) return;

        props.refreshProfiles(client!.id);
        setIsModalOpen(false);
    };

    const onFromJLSChange = (name: any, value: any) => {
        setFromJLS(value);
    }

    const onToJLSChange = (name: any, value: any) => {
        setToJLS(value);
    }

    const onSearchClicked = () => {
        search();
    };

    const search = () => {
        if (!client || !canBuyCoupon)
            return;

        if (!initialSearchDone)
            setInitialSearchDone(true);

        const searchCouponsRequest = {
            clientAdministrativeArea: client.address.administrativeAreaId,
            startAdministrativeAreas: [startAdministrativeArea || client.address.administrativeAreaId],
            endAdministrativeAreas: [endAdministrativeArea || startAdministrativeArea || client.address.administrativeAreaId],
            isIntendedForPrivilegedCoupons: false,
            profiles: client.clientProfiles?.map((clientProfile) => ({
                profileId: clientProfile?.profile?.id,
                clientProfileValidFrom: clientProfile.validFrom,
                clientProfileValidTo: clientProfile.validTo,
            } as ProfileQuery))
        } as SearchCouponsRequest;

        props.searchCoupons(searchCouponsRequest);
    }

    const onSearchSubsidisedClicked = () => {
        setSubsidisedCouponsMode(true);
        searchSubsidised();
    };

    function getStartOrEndAreasSubsidised(clientSubsidies: ClientSubsidy[], contractClientTariffs: ContractClientTariff[], end?: boolean): Array<number> {
        let areasArray: number[] = [];
        clientSubsidies?.forEach(subsidy => { areasArray.push(end ? subsidy.relation.endAdministrativeArea : subsidy.relation.startAdministrativeArea) });
        contractClientTariffs?.forEach(contract => { areasArray.push(end ? contract.relation.endAdministrativeArea : contract.relation.startAdministrativeArea) });
        return areasArray;
    }

    
      function getActiveOrFutureFirstActiveProfile(
        clientProfiles: ClientProfile[] | undefined,
      ): ClientProfile | undefined {
        if (!clientProfiles) return clientProfiles;
    
        const currentDate = new Date();
    
        const activeProfiles = clientProfiles.filter(
          (clientProfile) =>
            new Date(clientProfile.validFrom) <= currentDate &&
            new Date(clientProfile.validTo) >= currentDate,
        );
    
        if (activeProfiles.length) return activeProfiles[0];
    
        const futureActiveProfiles = clientProfiles.filter(
          (clientProfile) => new Date(clientProfile.validFrom) > currentDate,
        );
    
        if (futureActiveProfiles.length) {
          return futureActiveProfiles[0];
        }
    
        return undefined;
      }

    const searchSubsidised = () => {
        if (!client || !canBuyCoupon)
            return;

        if (!initialSearchDone)
            setInitialSearchDone(true);

        let organizationQuery: OrganizationQuery | undefined;
        const clientOrganization = client.clientOrganization;

        if (clientOrganization) {
            const organization = clientOrganization.organization;
            organizationQuery = {
                organizationGroupId: organization.organizationGroupId,
                organizationAdministrativeArea: organization.address.administrativeAreaId,
                clientOrganizationValidFrom: clientOrganization.validFrom,
                clientOrganizationValidTo: clientOrganization.validTo
            }
        }

        const clientArea = client.address.administrativeAreaId;
        const startAreas = getStartOrEndAreasSubsidised(clientSubsidies, client.contractClientTariffs);
        const endAreas = getStartOrEndAreasSubsidised(clientSubsidies, client.contractClientTariffs, true);

        const searchCouponsRequest = {
            clientAdministrativeArea: clientArea,
            startAdministrativeAreas: startAreas.length ? startAreas : [clientArea],
            endAdministrativeAreas: endAreas.length ? endAreas : [clientArea],
            isIntendedForPrivilegedCoupons: true,
            profiles: client.clientProfiles?.map((clientProfile) => ({
                profileId: clientProfile?.profile?.id,
                clientProfileValidFrom: clientProfile.validFrom,
                clientProfileValidTo: clientProfile.validTo,
            } as ProfileQuery)),
            organization: organizationQuery,
            subsidies: clientSubsidies?.map((subsidy) => ({
                subsidyId: subsidy.subsidy.id,
                relationId: subsidy.relation.id,
                clientSubsidyValidFrom: subsidy.validFrom,
                clientSubsidyValidTo: subsidy.validTo,
            } as SubsidyCouponQuery)),
            contracts: client.contractClientTariffs?.map((contract) => ({
                tariffId: contract.tariff.id,
                validFrom: contract.validFrom,
                rightRealizationValidTo: contract.rightRealizationValidTo,
                profileId: contract.profileId
            } as ContractCouponQuery)),
        } as SearchCouponsRequest;

        props.searchCoupons(searchCouponsRequest);
    }

    const onAnotherCouponClicked = () => {
        setSubsidisedCouponsMode(false);
        search();
    };

    const goToPurchase = (purchaseCoupon: Coupon) => {
        if (isFullProfileActivationInProgress) {
            Toast.notifyInfo(I18n.t('activateFullProfile.processStarted'));
            return;
        }
        props.purchaseCoupon({ couponHash: purchaseCoupon.hash, installments: false });
    };

    const purchaseCouponClicked = (coupon: Coupon) => {
        setSelectedCoupon(coupon);
        const warnings = checkCouponForWarnings(coupon, clientCoupons as Coupon[]);
        warnings?.length ? setShowWarning(true) : goToPurchase(coupon);
    };

    const onModalOkClicked = () => {
        setShowWarning(false);
        goToPurchase(selectedCoupon as Coupon);
    };

    const onModalCancelClicked = () => {
        setShowWarning(false);
        setSelectedCoupon(undefined);
    };

    const shouldSearchSubsidies = () => {
        return (client?.hasActiveClientSubsidies && clientSubsidies?.length) || (client?.hasActiveContractClientTariffs && client?.contractClientTariffs?.length);
    }

    useEffect(() => {
        if (shouldSearchSubsidies()) {
            onSearchSubsidisedClicked();
        }
        else {
            setSubsidisedCouponsMode(false);
            search();
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <ContentLayout titleTranslateId='myCoupons.title'>
            {
                client &&
                <>
                    <Row>
                        <Col xl={12} md={12} sm={12} className="pt-3 pb-3 border-bottom border-top">
                            <Row>
                                <Col xl={3} md={4} sm={12} className="mt-0 mb-0 d-flex align-items-center">
                                    <div className="align-items-center justify-content-center">
                                        <img src={icon_my_coupons} width="64" height="64" alt="Moji kuponi" />
                                    </div>
                                    <div className="pl-4">
                                        <span>{`${I18n.t('tickets.validCoupons')}:`}</span>
                                    </div>
                                </Col>
                                <Col xl={9} md={8} sm={12} className="mt-0 mb-0">
                                    <div id="myCoupons">
                                        <div>
                                            {validCoupons && !validCoupons.length && (
                                                <div className='mt-3'>
                                                    <p>{I18n.t('noActiveCoupons')}</p>
                                                </div>
                                            )}
                                        </div>
                                        {
                                            validCoupons && validCoupons.length > 0 &&
                                            <PrometTable
                                                data={validCoupons}
                                                columns={
                                                    [
                                                        {
                                                            cell: (d: Coupon) => (
                                                                <div>
                                                                    <div className='myCouponsContent float-left'>
                                                                        <div className='myCouponsItemPeriodName'>
                                                                            {getTicketPeriodName(d.validFrom, d.validTo)}
                                                                            <span className='myCouponsItemRelation'>
                                                                                {`: ${d.relation.caption}`}
                                                                            </span>
                                                                        </div>
                                                                    </div>

                                                                    <div className='clearfix'></div>
                                                                </div>
                                                            ),
                                                            align: 'left'
                                                        },
                                                        {
                                                            cell: (d: Coupon) => (
                                                                <div>
                                                                    <div className='myCouponsContent float-right'>
                                                                        <div className='myCouponsValid'>
                                                                            <div className='myCouponsItemPeriod'>
                                                                                {I18n.t('validFrom')}: {DateUtility.format(d.validFrom, 'dd/mm/yyyy')} - {DateUtility.format(d.validTo, 'dd/mm/yyyy')}
                                                                            </div>
                                                                        </div>
                                                                    </div>

                                                                    <div className='clearfix'></div>
                                                                </div>
                                                            ),
                                                            align: 'left'
                                                        },
                                                        {
                                                            cell: (d: Coupon) => (
                                                                <div>
                                                                    <div className='myCouponsContent float-right'>
                                                                        <div className='myCouponsContentRelationPrice'>
                                                                            <span className='myCouponsItemPrice'>
                                                                                {`${I18n.t('price')}:`}&nbsp;
                                                                            </span>
                                                                            <span className='myCouponsItemPrice font-weight-500 mr-1'>
                                                                                {`${NumberUtility.format(d.price || 0, 2, true, false, ',')} ${CurrencyUtility.getPrimaryCurrencyById(d.currencyId).sign}`}
                                                                            </span>
                                                                            <span className='myCouponsItemPrice euro-text'>
                                                                                {`(${EuroUtility.convertCurrency(d.price, d.currencyId)} ${CurrencyUtility.getSecondaryCurrencyById(d.currencyId).sign})`}
                                                                            </span>
                                                                        </div>
                                                                    </div>

                                                                    <div className='clearfix'></div>
                                                                </div>
                                                            ),
                                                            align: 'left'
                                                        },
                                                    ]
                                                }
                                                simpleTable
                                                hideFooter
                                                hideHeader
                                            />
                                        }
                                    </div>
                                </Col>

                                {!clientProfile && (
                                    <Row className="p-2 m-2">
                                        <Button
                                            type="button"
                                            color="primary"
                                            outline
                                            title={I18n.t("myProfile.tabs.myProfile.profiles.info")}
                                            onClick={() => setIsModalOpen(true)}
                                        >
                                            {I18n.t("myProfile.tabs.myProfile.profiles.refreshProfiles")}
                                        </Button>
                                    </Row>
                                )}
                            </Row>
                        </Col>
                    </Row>
                    {
                        !canBuyCoupon &&
                        <div className='d-flex justify-content-center my-3'><span>{I18n.t('tickets.notEligibleToPurchaseTheCoupon')}</span></div>
                    }
                    {
                        canBuyCoupon &&
                        <>
                            {
                                !subsidisedCouponsMode &&
                                <>
                                    <Row>
                                        <Col xl={3} lg={4} md={12} sm={12} className="mt-4 mb-2 d-flex align-items-center">
                                            <h6>{I18n.t('tickets.buy.buyTicketOrCoupon')}</h6>
                                        </Col>
                                        <Col xl={9} lg={8} md={12} sm={12} className="mt-4 mb-2">
                                            {
                                                ((client?.hasActiveClientSubsidies && clientSubsidies?.length) || (client?.hasActiveContractClientTariffs && client?.contractClientTariffs?.length)) &&
                                                <div className="text-background d-flex align-items-center justify-content-between">
                                                    <div className="float-left d-flex align-items-center">
                                                        <img src={icon_baseline_error} width="24" height="24" alt="!" />
                                                        <span className="pl-3 primary-color">
                                                            {I18n.t('tickets.buy.haveSubsidy')}
                                                        </span>
                                                    </div>
                                                    <div className="float-right d-flex align-items-center">
                                                        <span onClick={onSearchSubsidisedClicked} className="primary-color font-weight-500 cursor-pointer">
                                                            {I18n.t('tickets.buy.showSubsidisedCoupons')}
                                                        </span>
                                                    </div>
                                                </div>
                                            }
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col xl={12} md={12} sm={12} className="mt-4 mb-2 d-flex align-content-end">
                                            <span>{`${I18n.t('tickets.buy.selectTicketValidArea')}: `}</span>
                                            <img className="ml-2" src={icon_baseline_error} width="24" height="24" alt="!" />
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col xl={5} md={5} sm={10} className="mt-2 mb-2">
                                            <div id="SelectJLSFull" className='w-100'>
                                                <PrometSelect placeholder={I18n.t('tickets.fromPlaceholder')} name="fromJLS" onChange={onFromJLSChange} value={startAdministrativeArea || client.address?.administrativeAreaId} options={trafficAreaOptions} isSearchable={false} isClearable={false} />
                                            </div>
                                        </Col>
                                        <Col xl={5} md={5} sm={10} className="mt-2 mb-2">
                                            <div id="SelectJLSFull" className='w-100'>
                                                <PrometSelect placeholder={I18n.t('tickets.toPlaceholder')} name="toJLS" onChange={onToJLSChange} value={endAdministrativeArea} options={trafficAreaOptions} isSearchable={false} />
                                            </div>
                                        </Col>
                                        <Col xl={2} md={2} sm={2} className="mt-2 mb-2 d-none d-sm-block">
                                            <Button className='d-flex w-100 justify-content-center' type="button" color="primary" onClick={onSearchClicked}>{I18n.t('tickets.searchButton')}</Button>
                                        </Col>
                                        <Col xl={2} md={2} sm={2} className="mt-2 mb-2 d-block d-sm-none">
                                            <Button className='w-100 d-flex justify-content-center' type="button" color="primary" onClick={onSearchClicked}>{I18n.t('tickets.searchButton')}</Button>
                                        </Col>
                                    </Row>
                                </>
                            }
                            {
                                subsidisedCouponsMode &&
                                <div className='mt-4 mb-2'>
                                    <h6>{`${I18n.t('tickets.buy.availableSubsidyCoupons')}:`}</h6>
                                </div>
                            }
                            {
                                couponsSearchResult?.length ? (
                                    couponsSearchResult.map((coupon: Coupon) => (
                                        <CouponSearchItem key={coupon.hash} coupon={coupon} purchaseCouponClicked={() => purchaseCouponClicked(coupon)}></CouponSearchItem>
                                    ))
                                ) : initialSearchDone ?
                                    <div className='d-flex justify-content-center my-3'><span>{I18n.t('noResults')}</span></div>
                                    : <></>
                            }
                            {
                                subsidisedCouponsMode &&
                                <div>
                                    <div className='mt-4 mb-2'>
                                        <HorizontalDivider />
                                    </div>
                                    <div>
                                        <Button type="button" color="primary" outline onClick={onAnotherCouponClicked}>{I18n.t('tickets.anotherCouponButton')}</Button>
                                    </div>
                                </div>
                            }
                        </>
                    }
                </>
            }

            <PrometModal
                isOpen={showWarning}
                onOkClick={onModalOkClicked}
                onClose={onModalCancelClicked}
                footer
                headerText={I18n.t('tickets.buy.warning')}
                okText={I18n.t('yesNo.yes')}
                cancelText={I18n.t('yesNo.no')}
                size='lg'
            >
                {checkCouponForWarnings(selectedCoupon as Coupon, clientCoupons as Coupon[]).join('\r\n\r\n')}
            </PrometModal>

            
        <PrometModal
            isOpen={isModalOpen}
            onOkClick={checkStatus}
            onClose={() => setIsModalOpen(false)}
            headerText={I18n.t("myProfile.tabs.myProfile.profiles.refreshProfiles")}
            okText={I18n.t("myProfile.tabs.myProfile.profiles.refreshProfiles")}
            children={getModalBody()}
            footer
            size="lg"
        />
        </ContentLayout>
    );
}

const mapDispatchToProps = {
    searchCoupons: searchCouponsActions.request,
    purchaseCoupon: purchaseCouponActions.request,
    refreshProfiles: refreshProfilesActions.request
};

export default connect(null, mapDispatchToProps)(MyCoupons as any);
